import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { VersionService } from './version.service';
import { CurrentUserStateService } from './current-user-state.service';
import {
    DateRangeInterface,
    DisplayOptionsData,
    DisplayOptionStorage,
    IActiveDisplayOptions,
    IActiveDisplayOptions2,
    IHistoricalYears,
    IOptionalSummaryList,
    IPageDisplayOption,
    IScorecardDateRange,
    ISummaryAreaInfo,
} from '../interfaces/display-options';

@Injectable({
    providedIn: 'root',
})
export class DisplayOptionsService {
    private serverUrl = 'https://' + window.location.host;
    private version = '0.5'; // TODO Get version dynamically
    private _doUrl = (this.serverUrl + '/api/v{version}/displayoption').replace(
        '{version}',
        this.version
    );

    apiVersion: string;

    constructor(
        private http: HttpClient,
        private _versionService: VersionService,
        private _currentUserService: CurrentUserStateService
    ) {}

    getPageDisplayOptions(routerURL: string): Observable<IPageDisplayOption[]> {
        const params = new HttpParams().set('routerURL', routerURL);

        return this.http.get<IPageDisplayOption[]>(this._doUrl + '/GetPageDisplayOptions', {
            params: params,
        });
    }

    isNewHistoricaltrendYear() {
        let yearOptions: IHistoricalYears[] = JSON.parse(
            localStorage.getItem('historical-year-options')
        );

        if (yearOptions) {
            let currentTime = new Date();
            yearOptions.sort((a, b) => +b.year - +a.year);
            let thisYear = currentTime.getFullYear();
            return +yearOptions[0].year !== thisYear;
        } else {
            return true;
        }
    }

    setDefaultDisplayOptions() {
        let oldVersion = localStorage.getItem('version');
        if (this._currentUserService.isLoggedIn()) {
            this.setRecentYears();
            this._versionService.getAppVersion().subscribe(
                (res) => {
                    this.apiVersion = res.toString();
                    if (oldVersion != this.apiVersion) {
                        this.setInitialDisplayOptions();
                    }
                },
                (err) => {
                    throw new Error(err);
                }
            );
        }
    }

    setInitialDisplayOptions() {
        const params = {};
        //TODO: Move all related interfaces to a display options interface
        let defaultOptions = this.http.get<DisplayOptionStorage[]>(
            this._doUrl + '/GetDefaultDisplayOptions',
            {
                params: params,
            }
        );

        defaultOptions.subscribe(
            (res) => {
                res.map((item) => {
                    item.version = this.apiVersion;
                    item.optionName = item.pageUrl;
                });
                localStorage.setItem('tempDefaultDisplayOptionStorage', JSON.stringify(res));
                localStorage.setItem('version', this.apiVersion);
            },
            (err) => {
                localStorage.removeItem('version');
                throw new Error(err);
            }
        );
    }

    getScorecardRanges(fromDatePeriod: string): Observable<IScorecardDateRange[]> {
        const params = new HttpParams().set('fromDatePeriod', fromDatePeriod);
        return this.http.get<IScorecardDateRange[]>(this._doUrl + '/GetScorecardDates', {
            params: params,
        });
    }

    setRecentYears(yearsToGet: number = 5, endDate: any = null) {
        if (this.isNewHistoricaltrendYear()) {
            let yearOptions: IHistoricalYears[] = [];
            localStorage.removeItem('historical-year-options');
            this.getDateRanges('historical-trends', '', '', '').subscribe(
                (dates) => {
                    if (dates) {
                        let maxDate = new Date(
                            dates.find((item) => item.tag === 'TO').dateFullDescription
                        );
                        for (let i = 0; i < yearsToGet; i++) {
                            yearOptions.push({
                                year: (maxDate.getUTCFullYear() - i).toString(),
                                yearId: 'Year' + (yearsToGet - i).toString(),
                                yearInfo: {
                                    index: yearsToGet - i,
                                    visible: true,
                                },
                            });
                        }
                        localStorage.setItem(
                            'historical-year-options',
                            JSON.stringify(yearOptions)
                        );
                    }
                },
                (err) => {
                    throw new Error(err);
                }
            );
        }
    }

    getDateRanges(
        dashboard: string,
        datePeriod: string,
        priorCompareTo: string = 'Previous Period',
        selectedPeriod: string
    ): Observable<DateRangeInterface[]> {
        const params = new HttpParams()
            .set('dashboard', dashboard)
            .set('datePeriod', datePeriod)
            .set('priorCompareTo', priorCompareTo)
            .set('period', selectedPeriod);

        return this.http.get<DateRangeInterface[]>(this._doUrl + '/GetDateRange', {
            params: params,
        });
    }

    getDisplayoptionsFromGivenSproc(
        sprocInfo: string,
        displayoptionKey: number,
        hasDefaultSelection: boolean
    ): Observable<DisplayOptionsData[]> {
        const params = new HttpParams()
            .set('sprocInfo', sprocInfo)
            .set('displayoptionKey', displayoptionKey.toString())
            .set('hasDefaultSelection', hasDefaultSelection ? '1' : '0');
        return this.http.get<DisplayOptionsData[]>(
            this._doUrl + '/GetDisplayoptionsFromGivenSproc',
            { params: params }
        );
    }

    getAllUniqueDisplayOptionItems(): Observable<IActiveDisplayOptions[]> {
        return this.http.get<IActiveDisplayOptions[]>(this._doUrl + '/GetUniqueDisplayOptionItems');
    }

    //TODO: clean this
    updateCurrentDynamicDisplayOptions(uniqueDisplayOptionItems: IActiveDisplayOptions[]) {
        let allDefaultDisplayOptions: DisplayOptionStorage[] = JSON.parse(
            localStorage.getItem('tempDefaultDisplayOptionStorage')
        );

        let dynamicDiplayOptions = uniqueDisplayOptionItems.filter((item) => item.getDataFromSproc);
        let uniqeOptionNames = this.getUniqueItemList(dynamicDiplayOptions, 'mainOptionName');

        uniqeOptionNames.forEach((uniqueName) => {
            let dynamicDefaultOptions = allDefaultDisplayOptions.filter((item) =>
                item.optionValues.find((innerItem) => innerItem.optionKey === uniqueName)
            );
            let displayOptionsByName = dynamicDiplayOptions.filter(
                (item) => item.mainOptionName === uniqueName
            );

            dynamicDefaultOptions.forEach((defOpt) => {
                let optValue = defOpt.optionValues.find(
                    (innerItem) => innerItem.optionKey === displayOptionsByName[0].mainOptionName
                );

                if (optValue) {
                    let defaultSelectionPerOptionName: IActiveDisplayOptions =
                        displayOptionsByName.find((item) => item.isDefaultSelection === true);
                    let defaultSelection: DisplayOptionsData;

                    if (defaultSelectionPerOptionName) {
                        defaultSelection = {
                            mainOptionName: defaultSelectionPerOptionName.mainOptionName,
                            optionName: defaultSelectionPerOptionName.itemOptionName,
                            optionOrder: defaultSelectionPerOptionName.itemOptionOrder,
                            displayOptionKey: defaultSelectionPerOptionName.displayOptionKey,
                            friendlyName: defaultSelectionPerOptionName.itemFriendlyName,
                            summaryDisplayName: defaultSelectionPerOptionName.summaryDisplayName
                                ? defaultSelectionPerOptionName.summaryDisplayName
                                : defaultSelectionPerOptionName.itemFriendlyName,
                            isDefaultSelection: defaultSelectionPerOptionName.isDefaultSelection,
                        };
                    }

                    if (
                        !defaultSelectionPerOptionName &&
                        displayOptionsByName[0].isMultiSelection
                    ) {
                        optValue.selectedOptions = 'All';
                        optValue.selectedOptionsFriendly = 'All';
                        optValue.selectedOptionList = [];
                    } else if (defaultSelectionPerOptionName) {
                        optValue.selectedOptions = defaultSelectionPerOptionName.itemOptionName;
                        optValue.selectedOptionList = [defaultSelection];
                        optValue.selectedOptionsFriendly =
                            defaultSelectionPerOptionName.mainOptionName === 'yearsDdl'
                                ? ''
                                : defaultSelection.summaryDisplayName;
                    }
                }
            });
        });

        localStorage.setItem(
            'tempDefaultDisplayOptionStorage',
            JSON.stringify(allDefaultDisplayOptions)
        );
    }

    getUniqueItemList(array: Array<any>, columnNameToFindUniqueVal: string): Array<any> {
        return array
            .map((item) => item[columnNameToFindUniqueVal])
            .filter((value, index, self) => self.indexOf(value) === index);
    }

    syncYearTogalWithDrilldownDisplayoptions() {
        let yearItems: IHistoricalYears[] = JSON.parse(
            localStorage.getItem('historical-year-options')
        );
        let allDefaultDisplayOptions: DisplayOptionStorage[] = JSON.parse(
            localStorage.getItem('tempDefaultDisplayOptionStorage')
        );

        if (allDefaultDisplayOptions) {
            let yearDdlDefaultOptions = allDefaultDisplayOptions.filter((item) =>
                item.optionValues.find((subItem) => subItem.optionKey === 'yearsDdl')
            );

            yearDdlDefaultOptions.forEach((opt) => {
                let option = opt.optionValues.find((item) => item.optionKey === 'yearsDdl');

                option.selectedOptionList = [];
                for (let yearInf of yearItems) {
                    if (yearInf.yearInfo.visible) {
                        option.selectedOptionList.push({
                            mainOptionName: option.optionKey,
                            optionName: yearInf.yearId,
                            optionOrder: yearInf.yearInfo.index,
                            isDefaultSelection: false,
                            friendlyName: yearInf.yearId,
                            summaryDisplayName: '',
                        });
                    }
                }
            });

            localStorage.setItem(
                'tempDefaultDisplayOptionStorage',
                JSON.stringify(allDefaultDisplayOptions)
            );
        }
    }

    updateExportSummaryList(url: string, optionalSummaryList: IOptionalSummaryList[]) {
        let exportHeaderSummaryData: ISummaryAreaInfo[] = [];
        let pageDspOpt: DisplayOptionStorage;
        let displayOptionsLocalStorage: DisplayOptionStorage[] = JSON.parse(
            localStorage.getItem('displayOptionsStorage')
        );
        if (displayOptionsLocalStorage) {
            pageDspOpt = displayOptionsLocalStorage.find(
                (option) => option.optionName.toLowerCase() === url.toLowerCase()
            );
        }

        if (!pageDspOpt) {
            let tempDefaultDisplayOptionStorage: DisplayOptionStorage[] = JSON.parse(
                localStorage.getItem('tempDefaultDisplayOptionStorage')
            );

            pageDspOpt = tempDefaultDisplayOptionStorage.find(
                (item) => item.optionName.toLowerCase() === url.toLowerCase()
            );
        }

        if (pageDspOpt) {
            if (pageDspOpt.optionValues) {
                pageDspOpt.optionValues.forEach((item) => {
                    if (
                        item.isSelectSummaryShow &&
                        item.selectedOptionsFriendly != '' &&
                        item.isSelectSummaryShowInDrilldown
                    ) {
                        exportHeaderSummaryData.push({
                            title: item.summarySectionTitleName,
                            valueList: [
                                {
                                    value: item.selectedOptionsFriendly,
                                },
                            ],
                        });
                    }
                });
            }
        }

        if (optionalSummaryList && optionalSummaryList.length > 0) {
            optionalSummaryList.forEach((item) => {
                exportHeaderSummaryData.push({
                    title: item.title,
                    valueList: [
                        {
                            value: item.value,
                        },
                    ],
                });
            });
        }
        return exportHeaderSummaryData;
    }
}
